import clsx from 'clsx';
import { type ReactElement, useState } from 'react';

import CardImage from '@/components/CardImage/CardImage';
import useCardLookup from '@/hooks/useCardLookup';
import { getDisplayImageUris } from '@/lib/cardUtils';

import type { PickRecord } from '../types';
import DraftCardGrid from './DraftCardGrid';

interface PickTimelineProps {
  picks: PickRecord[];
}

export default function PickTimeline({ picks }: PickTimelineProps): ReactElement {
  const [expandedPick, setExpandedPick] = useState<number | null>(null);
  const { getFullCard } = useCardLookup();

  if (picks.length === 0) {
    return <p className="text-sm text-zinc-500">No picks recorded</p>;
  }

  return (
    <div className="space-y-2">
      {picks.map((pick, index) => {
        const isExpanded = expandedPick === index;
        return (
          <div key={index} className="rounded-lg border border-zinc-700/50 bg-zinc-800/30">
            <button
              onClick={() => setExpandedPick(isExpanded ? null : index)}
              className="flex w-full items-center gap-3 px-3 py-2 text-left"
            >
              {/* Pick info */}
              <span className="shrink-0 rounded bg-zinc-700 px-2 py-0.5 text-xs font-medium text-zinc-300">
                P{pick.packIndex + 1}p{pick.pickIndex + 1}
              </span>

              {/* Picked card thumbnail */}
              <div className="flex shrink-0 gap-1">
                {pick.picked.map((card) => {
                  const fullCard = getFullCard(card.id);
                  const imageUris = fullCard ? getDisplayImageUris(fullCard) : card.image_uris;
                  return (
                    <div key={card.id} className="w-10">
                      <CardImage imageUris={imageUris} name={card.name} size="small" className="rounded" />
                    </div>
                  );
                })}
              </div>

              {/* Card names */}
              <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">{pick.picked.map((c) => c.name).join(', ')}</span>

              {/* Expand indicator */}
              <span className={clsx('text-xs text-zinc-500 transition-transform', isExpanded && 'rotate-180')}>▼</span>
            </button>

            {/* Expanded: show full pack */}
            {isExpanded && (
              <div className="border-t border-zinc-700/50 px-3 py-3">
                <p className="mb-2 text-xs text-zinc-500">Available ({pick.availableCards.length} cards):</p>
                <DraftCardGrid cards={pick.availableCards} highlightedIds={new Set(pick.picked.map((p) => p.id))} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
